import { useState, useEffect } from "react";
import { supabase, SanPhamHanQuoc } from "../lib/supabase";
import { createSlug } from "../lib/slug";
import { Product, getProductById } from "../mocks/products";
import { BuilderPage } from "../pages/admin/components/builder/types";

// ── Robust JSON / array parsing ─────────────────────────────────────────────
function safeParseArray<T>(val: unknown): T[] {
  if (Array.isArray(val)) return val as T[];
  if (typeof val === "string" && val.trim()) {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed as T[];
    } catch {
      // ignore
    }
  }
  return [];
}

function safeParseStringArray(val: unknown): string[] {
  const arr = safeParseArray<unknown>(val);
  return arr.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
}

function safeParseMediaItems(val: unknown): Array<{ url: string; type: string; is_cover?: boolean }> {
  const arr = safeParseArray<Record<string, unknown>>(val);
  return arr
    .filter((item) => typeof item.url === "string" && item.url.trim().length > 0)
    .map((item) => ({ url: item.url as string, type: String(item.type ?? "image"), is_cover: !!item.is_cover }));
}

function safeParseBuilderPages(val: unknown): BuilderPage[] {
  const arr = safeParseArray<unknown>(val);
  return arr.filter((p): p is BuilderPage => typeof p === "object" && p !== null && "id" in p && "blocks" in p);
}

function safeParseSections(val: unknown): Product["sections"] {
  const arr = safeParseArray<unknown>(val);
  return arr.filter((s): s is NonNullable<Product["sections"]>[number] => typeof s === "object" && s !== null && "title" in s) as Product["sections"];
}

function safeParsePatents(val: unknown): Product["patents_certifications"] {
  const arr = safeParseArray<unknown>(val);
  return arr.filter((p): p is NonNullable<Product["patents_certifications"]>[number] => typeof p === "object" && p !== null && "title" in p) as Product["patents_certifications"];
}

function safeParseApplications(val: unknown): Product["applications"] {
  const arr = safeParseArray<unknown>(val);
  return arr.filter((a): a is NonNullable<Product["applications"]>[number] => typeof a === "object" && a !== null && "title" in a) as Product["applications"];
}

function safeParseSpecs(val: unknown): Array<{ label: string; value: string }> {
  const arr = safeParseArray<Record<string, unknown>>(val);
  return arr
    .filter((s) => typeof s.label === "string" && typeof s.value === "string")
    .map((s) => ({ label: s.label as string, value: s.value as string }));
}

/**
 * Maps a Supabase row to our frontend Product type.
 * Falls back gracefully when fields are missing.
 */
function mapToProduct(row: SanPhamHanQuoc): Product {
  const rawRow = row as Record<string, unknown>;
  // media_items: array of {url, type} stored in Supabase
  const mediaItems = safeParseMediaItems(rawRow.media_items);
  const images = safeParseStringArray(rawRow.images);
  const builderPages = safeParseBuilderPages(rawRow.builder_pages);
  const catalogBuilderPages = safeParseBuilderPages(rawRow.catalog_builder_pages);
  const sections = safeParseSections(rawRow.sections);
  const patents = safeParsePatents(rawRow.patents_certifications);
  const applications = safeParseApplications(rawRow.applications);
  const specs = safeParseSpecs(rawRow.specs);
  const specsKr = safeParseSpecs(rawRow.specs_kr);
  const tags = safeParseStringArray(rawRow.tags);
  const tagsKr = safeParseStringArray(rawRow.tags_kr);
  const pdfPreviewImages = safeParseStringArray(rawRow.pdf_preview_images);
  const flipbookImages = safeParseStringArray(rawRow.flipbook_images);

  return {
    id: row.id,
    name: row.name ?? "",
    nameKr: row.name_kr ?? "",
    categoryId: row.category_id ?? "",
    categoryName: row.category_name ?? "",
    categoryNameKr: row.category_name_kr ?? "",
    subCategoryId: row.sub_category_id ?? "",
    subCategoryName: row.sub_category_name ?? "",
    subCategoryNameKr: row.sub_category_name_kr ?? "",
    companyName: row.company_name ?? "",
    companyCode: row.company_code ?? "",
    companyId: row.company_id ?? undefined,
    companyLogo: row.company_logo ?? undefined,
    managerCode: row.manager_code ?? "",
    managerPhone: row.manager_phone ?? "",
    managerEmail: row.manager_email ?? "",
    price: row.price ?? "Liên hệ để biết giá",
    description: row.description ?? "",
    descriptionKr: row.description_kr ?? "",
    descriptionDetail: row.description_detail ?? "",
    descriptionDetailKr: row.description_detail_kr ?? "",
    images,
    // Pass media_items through so ProductHero can render videos
    media_items: mediaItems.length > 0 ? mediaItems : undefined,
    registeredDate: row.registered_date ?? "",
    tags,
    tagsKr,
    isFeatured: row.is_featured ?? false,
    originalLink: row.original_link ?? undefined,
    cardLink: row.card_link ?? undefined,
    heroTagline: row.hero_tagline ?? undefined,
    heroTaglineKr: row.hero_tagline_kr ?? "",
    sections,
    patents_certifications: patents,
    applications,
    builder_pages: builderPages.length > 0 ? builderPages : undefined,
    catalog_builder_pages: catalogBuilderPages.length > 0 ? catalogBuilderPages : undefined,
    flipbook_images: flipbookImages.length > 0 ? flipbookImages : undefined,
    display_mode: (rawRow.display_mode as string) || "default",
    pdf_url: (rawRow.pdf_url as string) || undefined,
    pdf_pages: Array.isArray(rawRow.pdf_pages) ? (rawRow.pdf_pages as number[]) : undefined,
    pdf_preview_images: pdfPreviewImages,
    external_url: (rawRow.external_url as string) || undefined,
    comments: [],
    specs,
    specsKr,
  };
}

interface UseProductReturn {
  product: Product | null;
  loading: boolean;
  source: "supabase" | "mock" | null;
}

/**
 * Fetches a product by ID.
 * Priority: Supabase table "san_pham_han_quoc" → mock data fallback.
 */
export const useProduct = (slugOrId: string | undefined): UseProductReturn => {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<"supabase" | "mock" | null>(null);

  useEffect(() => {
    if (!slugOrId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchProduct = async () => {
      setLoading(true);

      // 1. Try Supabase by ID first (backward compat with UUIDs)
      try {
        const { data, error } = await supabase
          .from("san_pham_han_quoc")
          .select("*")
          .eq("id", slugOrId)
          .maybeSingle();

        if (!cancelled) {
          if (!error && data) {
            setProduct(mapToProduct(data as SanPhamHanQuoc));
            setSource("supabase");
            setLoading(false);
            return;
          }
        }
      } catch {
        // fall through
      }

      // 2. Try Supabase by slug (match product name slug)
      try {
        const { data, error } = await supabase
          .from("san_pham_han_quoc")
          .select("*");

        if (!cancelled && !error && data) {
          const match = (data as SanPhamHanQuoc[]).find(
            (row) => createSlug(row.name) === slugOrId
          );
          if (match) {
            setProduct(mapToProduct(match));
            setSource("supabase");
            setLoading(false);
            return;
          }
        }
      } catch {
        // fall through
      }

      // 3. Fallback to mock data (by id or slug)
      if (!cancelled) {
        let mock = getProductById(slugOrId);
        if (!mock) {
          // try by slug
          const { products: allMock } = await import("../mocks/products");
          mock = allMock.products.find(
            (p) => createSlug(p.name) === slugOrId
          ) ?? null;
        }
        setProduct(mock ?? null);
        setSource(mock ? "mock" : null);
        setLoading(false);
      }
    };

    fetchProduct();

    return () => {
      cancelled = true;
    };
  }, [slugOrId]);

  return { product, loading, source };
};

/**
 * Fetches a product by companySlug + productSlug.
 * Match: companySlug = createSlug(company_name), productSlug = createSlug(name).
 */
export const useProductBySlug = (
  companySlug: string | undefined,
  productSlug: string | undefined
): UseProductReturn => {
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState<"supabase" | "mock" | null>(null);

  useEffect(() => {
    if (!productSlug) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const fetchProduct = async () => {
      setLoading(true);

      // 1. Try Supabase by both companySlug + productSlug
      try {
        const { data, error } = await supabase
          .from("san_pham_han_quoc")
          .select("*");

        if (!cancelled && !error && data) {
          const match = (data as SanPhamHanQuoc[]).find((row) => {
            const rowCompanySlug = createSlug(row.company_name ?? "");
            const rowProductSlug = createSlug(row.name ?? "");
            return rowProductSlug === productSlug &&
              (!companySlug || rowCompanySlug === companySlug);
          });
          if (match) {
            setProduct(mapToProduct(match));
            setSource("supabase");
            setLoading(false);
            return;
          }
        }
      } catch {
        // fall through
      }

      // 2. Fallback: search by productSlug only (if companySlug not found)
      try {
        const { data, error } = await supabase
          .from("san_pham_han_quoc")
          .select("*");

        if (!cancelled && !error && data) {
          const match = (data as SanPhamHanQuoc[]).find(
            (row) => createSlug(row.name ?? "") === productSlug
          );
          if (match) {
            setProduct(mapToProduct(match));
            setSource("supabase");
            setLoading(false);
            return;
          }
        }
      } catch {
        // fall through
      }

      // 3. Fallback to mock data
      if (!cancelled) {
        const { products: allMock } = await import("../mocks/products");
        let mock = allMock.products.find((p) => {
          const pSlug = createSlug(p.name);
          const cSlug = createSlug(p.companyName ?? "");
          return pSlug === productSlug && (!companySlug || cSlug === companySlug);
        });
        if (!mock && companySlug) {
          mock = allMock.products.find(
            (p) => createSlug(p.name) === productSlug
          ) ?? null;
        }
        setProduct(mock ?? null);
        setSource(mock ? "mock" : null);
        setLoading(false);
      }
    };

    fetchProduct();

    return () => {
      cancelled = true;
    };
  }, [companySlug, productSlug]);

  return { product, loading, source };
};

/**
 * Fetches ALL products from Supabase.
 * Falls back to mock data list.
 */
export const useProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchProducts = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("san_pham_han_quoc")
          .select("*")
          .order("created_at", { ascending: false });

        if (!cancelled) {
          if (!error && data && (data as SanPhamHanQuoc[]).length > 0) {
            setProducts((data as SanPhamHanQuoc[]).map(mapToProduct));
            setLoading(false);
            return;
          }
        }
      } catch {
        // fall through
      }

      // Fallback to static mock products
      if (!cancelled) {
        const { products: mockProducts } = await import("../mocks/products");
        setProducts(mockProducts);
        setLoading(false);
      }
    };

    fetchProducts();
    return () => {
      cancelled = true;
    };
  }, []);

  return { products, loading };
};

/**
 * Fetches newest products (sorted by created_at desc, max 4) for "Sản phẩm mới".
 * Fetches older products (the rest) for "Sản phẩm khác".
 */
export const useNewestProducts = (limit = 4) => {
  const [newest, setNewest] = useState<Product[]>([]);
  const [others, setOthers] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchProducts = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("san_pham_han_quoc")
          .select("*")
          .order("created_at", { ascending: false });

        if (!cancelled && !error && data && (data as SanPhamHanQuoc[]).length > 0) {
          const all = (data as SanPhamHanQuoc[]).map(mapToProduct);
          setNewest(all.slice(0, limit));
          setOthers(all.slice(limit));
          setLoading(false);
          return;
        }
      } catch {
        // fall through
      }

      if (!cancelled) {
        const { products: mockProducts } = await import("../mocks/products");
        setNewest(mockProducts.slice(0, limit));
        setOthers(mockProducts.slice(limit));
        setLoading(false);
      }
    };

    fetchProducts();
    return () => { cancelled = true; };
  }, [limit]);

  return { newest, others, loading };
};

/**
 * Fetches featured products (legacy - kept for backward compat).
 */
export const useFeaturedProducts = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchFeatured = async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase
          .from("san_pham_han_quoc")
          .select("*")
          .order("created_at", { ascending: false });

        if (!cancelled) {
          if (!error && data && (data as SanPhamHanQuoc[]).length > 0) {
            setProducts((data as SanPhamHanQuoc[]).map(mapToProduct));
            setLoading(false);
            return;
          }
        }
      } catch {
        // fall through
      }

      if (!cancelled) {
        const { products: mockProducts } = await import("../mocks/products");
        setProducts(mockProducts);
        setLoading(false);
      }
    };

    fetchFeatured();
    return () => {
      cancelled = true;
    };
  }, []);

  return { products, loading };
};
